import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { sa, SaButton, Field, errMsg } from './ui';

const FIELDS = [
  ['name', 'Platform name'], ['tagline', 'Tagline'], ['domain', 'Platform domain (tenant storefronts live at {slug}.domain)'],
  ['app_host', 'Staff application host'], ['admin_host', 'Super Admin console host'],
  ['marketing_hosts', 'Marketing site hosts (comma separated)'], ['support_email', 'Support email (help@)'], ['sales_email', 'Sales email (demo requests go here)'],
];
const LEAD_STATUSES = ['new', 'contacted', 'booked', 'won', 'lost'];
const STATUS_COLOR = { new: '#fbbf24', contacted: '#60a5fa', booked: '#a78bfa', won: '#22c55e', lost: '#71717A' };

const LeadRow = ({ l, onChanged }) => {
  const [notes, setNotes] = useState(l.notes || '');
  const patch = async (body) => { try { await axios.patch(`/superadmin/platform/leads/${l.id}`, body); toast.success('Lead updated'); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid={`platform-lead-${l.id}`} style={{ borderBottom: '1px solid #27272A', padding: '12px 0', fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select data-testid={`lead-status-${l.id}`} value={l.status || 'new'} onChange={(e) => patch({ status: e.target.value })} style={{ ...sa.input, width: 'auto', padding: '4px 8px', fontSize: 11, textTransform: 'uppercase', color: STATUS_COLOR[l.status] || '#fff', fontWeight: 700 }}>{LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <strong>{l.company_name}</strong> · {l.full_name} · <a href={`mailto:${l.email}`} style={{ color: '#A1A1AA' }}>{l.email}</a>{l.phone && <span style={{ color: '#A1A1AA' }}> · {l.phone}</span>}
      </div>
      <div style={{ color: '#71717A', fontSize: 12, marginTop: 4 }}>{l.shop_type} · {l.monthly_volume}{l.booking_start ? ` · demo ${new Date(l.booking_start).toLocaleString()} (${l.booking_status || 'scheduled'})` : l.preferred_time ? ` · prefers ${l.preferred_time}` : ''} · {new Date(l.at).toLocaleString()}
        {l.notified && <span data-testid={`lead-notified-${l.id}`}> · emails: sales {l.notified.sales ? '✓' : '✗'} / customer {l.notified.customer ? '✓' : '✗'}{l.sales_to ? ` (→ ${l.sales_to})` : ''}</span>}</div>
      {l.message && <div style={{ color: '#D4D4D8', marginTop: 4, whiteSpace: 'pre-wrap' }}>{l.message}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input data-testid={`lead-notes-${l.id}`} style={{ ...sa.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="Internal notes (call outcome, booked date…)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <SaButton variant="ghost" data-testid={`lead-save-notes-${l.id}`} onClick={() => patch({ notes })} style={{ padding: '4px 10px' }}>Save</SaButton>
      </div>
      {l.updated_by && <div style={{ color: '#52525B', fontSize: 11, marginTop: 4 }}>Updated {new Date(l.updated_at).toLocaleString()} by {l.updated_by}</div>}
    </div>
  );
};

export const PlatformSettings = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  const load = async () => {
    try {
      const [{ data: s }, { data: l }] = await Promise.all([axios.get('/superadmin/platform'), axios.get('/superadmin/platform/leads')]);
      setData(s); setLeads(l); setDraft({});
    } catch (e) { toast.error(errMsg(e, 'Failed to load platform settings')); }
  };
  useEffect(() => { load(); }, []);

  const value = (k) => (draft[k] !== undefined ? draft[k] : (Array.isArray(data?.settings?.[k]) ? data.settings[k].join(', ') : data?.settings?.[k] ?? ''));
  const save = async () => {
    if (!Object.keys(draft).length) return toast.info('Nothing to save');
    setSaving(true);
    try { await axios.patch('/superadmin/platform', draft); toast.success('Platform settings saved'); await load(); }
    catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setSaving(false); }
  };

  if (!data) return <p style={{ color: '#A1A1AA' }}>Loading…</p>;
  const s = data.settings;
  return (
    <div data-testid="platform-settings" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
      <div style={{ ...sa.surface, padding: 24 }}>
        <h4 style={{ ...sa.heading, fontSize: 22, marginTop: 0 }}>Platform domains</h4>
        <p style={{ color: '#A1A1AA', fontSize: 13 }}>Env values are the defaults; anything saved here overrides them. Changing hosts requires matching DNS + TLS on the hosting side.</p>
        {FIELDS.map(([k, label]) => (
          <Field key={k} label={label}><input data-testid={`platform-${k}`} style={sa.input} value={value(k)} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} /></Field>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, padding: '6px 0 14px', cursor: 'pointer' }}>
          <input type="checkbox" data-testid="platform-strict_hosts" checked={draft.strict_hosts ?? Boolean(s.strict_hosts)} onChange={(e) => setDraft((d) => ({ ...d, strict_hosts: e.target.checked }))} />
          Strict hosts — unknown hostnames fail closed (recommended in production)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, padding: '0 0 14px', cursor: 'pointer' }}>
          <input type="checkbox" data-testid="platform-self_signup" checked={draft.self_signup ?? (s.self_signup !== false)} onChange={(e) => setDraft((d) => ({ ...d, self_signup: e.target.checked }))} />
          Self-serve signup — allow new companies to start a 14-day trial from /signup
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><SaButton data-testid="platform-save-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save platform settings'}</SaButton></div>
      </div>
      <div style={{ display: 'grid', gap: 24, alignContent: 'start' }}>
        <div style={{ ...sa.surface, padding: 24 }}>
          <h4 style={{ ...sa.heading, fontSize: 22, marginTop: 0 }}>Host map</h4>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }} data-testid="platform-host-map">
            <tbody>
              {[['Marketing site', (s.marketing_hosts || []).join(', ')], ['Staff application', s.app_host], ['Super Admin console', s.admin_host],
                ['Company storefronts', s.domain ? `{slug}.${s.domain} or the company's custom domain` : '—'], ['Preview / dev hosts', (s.dev_hosts || []).join(', ')]].map(([l, v]) => (
                <tr key={l} style={{ borderBottom: '1px solid #27272A' }}><td style={{ ...sa.overline, padding: '10px 0', width: 180 }}>{l}</td><td style={{ padding: '10px 0', fontFamily: 'monospace' }}>{v || '—'}</td></tr>
              ))}
            </tbody>
          </table>
          <p style={{ color: '#71717A', fontSize: 12, marginTop: 12 }}>Reserved subdomains: {data.reserved_subdomains.join(', ')}</p>
        </div>
        <div style={{ ...sa.surface, padding: 24 }}>
          <h4 style={{ ...sa.heading, fontSize: 22, marginTop: 0 }}>Demo requests ({leads.length})</h4>
          <p style={{ color: '#71717A', fontSize: 12, marginTop: -8 }}>{LEAD_STATUSES.map((s) => `${s}: ${leads.filter((l) => (l.status || 'new') === s).length}`).join(' · ')}</p>
          {leads.length === 0 ? <p style={{ color: '#A1A1AA', fontSize: 13 }}>No demo requests yet.</p> : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }} data-testid="platform-leads">
              {leads.map((l) => <LeadRow key={l.id} l={l} onChanged={load} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
